/**
 * Registers a research job with the shell once it has COMPLETED with matches.
 *
 * Ported from the deleted InvestigatorWorkspace. Registering attaches nothing:
 * the chat's "Search within N ... results" button does that, on an explicit click.
 */
import { useEffect, useRef, useState } from 'react';
import { fetchJobProgress } from '../services/activeResult';
import type { JobProgress } from '../services/watchedJobs';
import { useTab } from './TabContext';

/** How often the hook re-checks the run it is reporting. */
const POLL_MS = 5000;
const UNSETTLED = ['pending', 'running', 'analyzing', 'resuming'];

export function useReportResearchRun(
  jobId: string | null | undefined,
  /** `null` = not known yet: nothing is reported until it is (a report cannot be corrected later). */
  queryText: string | null = '',
): { running: boolean } {
  const { reportRun } = useTab();
  const [progress, setProgress] = useState<{ jobId: string; p: JobProgress } | null>(null);
  // A different run replaces what we are describing: never read the old one's progress.
  const current = progress && progress.jobId === jobId ? progress.p : null;

  // Poll only while a run is on screen and not yet settled. `null` from a
  // network blip leaves the last reading standing rather than blanking it.
  const settled = current !== null && !UNSETTLED.includes(current.status);
  useEffect(() => {
    if (!jobId || settled) return undefined;
    let cancelled = false;
    const tick = () => {
      void fetchJobProgress(jobId).then((p) => {
        if (!cancelled && p) setProgress({ jobId, p });
      });
    };
    tick();
    const timer = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [jobId, settled]);

  // Only a COMPLETED run is reported. A running or failed one would hand the
  // chat an empty result set, which reads as "the investigation found nothing".
  const complete = current?.status === 'complete';

  // Report each finished run exactly once, keyed on the JOB ID: `complete`
  // stays true across re-renders, and every report mints a new seq that would
  // reset the chat's "Added" state. A run that matched nothing is not reported.
  const reportedJob = useRef<string | null>(null);
  useEffect(() => {
    if (!jobId || !complete || queryText === null) return;
    if (reportedJob.current === jobId) return;
    const count = current?.found ?? 0;
    if (count <= 0) return;
    reportedJob.current = jobId;
    reportRun({ kind: 'job', jobId, count, queryText });
  }, [jobId, complete, current, queryText, reportRun]);

  return { running: current !== null && !settled };
}
